import { config, TWIN_TYPE } from './config';

export const spousePath = (
  uNode,
  cNode,
  parentRelationship = 'married',
  isConsanguineous = false,
  laneY = null
) => {
  const { x: ux, y: uy } = uNode;
  const { x: cx, y: cy } = cNode;

  // Gap between the two parallel lines of a consanguineous union. Tied to the
  // symbol size (which scales with zoom) rather than a fixed 15px: the
  // relationship stroke is non-scaling (~constant on screen), so a fixed
  // coordinate-space gap shrinks below the line width when zoomed out and the
  // two lines merge into one. A symbol-proportional gap stays distinguishable
  // at any zoom.
  const line_gap = config.SYMBOL_SIZE * 0.2;

  // STRETCHED route (a decoupled SECONDARY containment bridge whose partners sit in
  // different family columns — G-1-01373's 25-35): drop each partner to a horizontal lane
  // BELOW the row, run the long marriage bar there, and come back up to the other partner.
  // This keeps the bar off the row of nodes it would otherwise run straight over. The
  // shared child drops from the bar (its union node sits ON the lane). divorced/separated
  // ticks are omitted for the stretched form (it is a distant-cousin bar, not a divorce).
  if (laneY != null && Number.isFinite(laneY) && Math.abs(ux - cx) >= 1) {
    let path = `M${ux},${uy} L${ux},${laneY} L${cx},${laneY} L${cx},${cy}`;
    if (isConsanguineous) {
      const g = line_gap;
      path += ` M${ux},${uy} L${ux},${laneY + g} L${cx},${
        laneY + g
      } L${cx},${cy}`;
    }
    return path;
  }

  let path = `M${ux},${uy} L${cx},${cy}`;
  if (isConsanguineous) {
    path += ` M${ux},${uy - line_gap} L${cx},${cy - line_gap}`;
  }

  const mx = (ux + cx) / 2;
  const my = (uy + cy) / 2;
  const line_height = 32;

  if (parentRelationship === 'divorced') {
    path +=
      ` M${mx + 20},${my - line_height} L${mx - 20},${my + line_height}` +
      ` M${mx + 40},${my - line_height} L${mx},${my + line_height}`;
  } else if (parentRelationship === 'separated') {
    path += ` M${mx + 20},${my - line_height} L${mx - 20},${my + line_height}`;
  }

  return path;
};

export const childPath = (uNode, cNode, laneY = null) => {
  let d;
  const { x: ux, y: uy } = uNode;
  const { x: cx, y: cy } = cNode;

  // offset is set to zero for dev purpose
  const offSet = 0;

  // OVER-THE-TOP route (a displaced sibship whose connector would otherwise sweep an
  // unrelated co-generation): the child sits IN LINE with its generation and the
  // sibling-line runs HIGHER, parallel to and above the generation row, at laneY (above
  // the union). Up from the union, across at laneY, then down to the child.
  if (laneY != null && Math.abs(ux - cx) >= 1) {
    return `M${ux},${uy} L${ux},${laneY} L${cx},${laneY} L${cx},${cy - offSet}`;
  }

  // Sub-pixel float error from layout arithmetic (e.g. 6600 vs 6599.999999999999)
  // must not turn a genuinely-vertical drop into a dog-leg; a 1px tolerance is far
  // below the symbol-width gap of any real dog-leg.
  if (Math.abs(ux - cx) < 1) {
    // Case 1: same x → straight vertical
    d = `M${ux},${uy} L${ux},${cy - offSet}`;
  } else {
    // Case 2: different x → elbow
    const midY = (uy + cy) / 2;
    d = `M${ux},${uy} 
         L${ux},${midY} 
         L${cx},${midY} 
         L${cx},${cy - offSet}`;
  }

  return d;
};

export const twinGroupPath = (unionNode, twinNodes) => {
  if (!twinNodes || twinNodes.length === 0) {
    return { path: '', unknownMarkers: [] };
  }

  const { x: ux, y: uy } = unionNode;

  // Calculate center point between all twins (horizontal center)
  const twinXPositions = twinNodes.map((t) => t.x);
  const minX = Math.min(...twinXPositions);
  const maxX = Math.max(...twinXPositions);
  const centerX = (minX + maxX) / 2;

  // Calculate meeting point Y (halfway between union and twins)
  const twinY = twinNodes[0].y;
  const meetingHeightRatio = config.TWIN_DIAGONAL_MEETING_HEIGHT;
  const meetingY = uy + (twinY - uy) * meetingHeightRatio;

  // bar position
  const barHeightRatio = 0.5;
  const barY = meetingY + (twinY - meetingY) * barHeightRatio;

  let path = '';

  // Elbow line from union node down to meeting point
  // Vertical down from union, then horizontal to center
  path += `M ${ux},${uy} L ${ux},${meetingY} L ${centerX},${meetingY}`;

  // Diagonal lines from meeting point to each twin
  twinNodes.forEach((twin) => {
    path += ` M ${centerX},${meetingY} L ${twin.x},${twin.y}`;
  });

  const processedPairs = new Set();
  const unknownMarkers = [];

  // Helper to calculate x position on diagonal line at given y
  const getXOnDiagonal = (twinX, twinY) => {
    if (twinY === meetingY) return twinX; // Avoid division by zero
    return (
      centerX + ((barY - meetingY) * (twinX - centerX)) / (twinY - meetingY)
    );
  };

  twinNodes.forEach((twin) => {
    if (!twin.twinRelations || !Array.isArray(twin.twinRelations)) return;

    twin.twinRelations.forEach((rel) => {
      const pairKey = [twin.pid, rel.withPid].sort().join('-');
      if (processedPairs.has(pairKey)) return;

      const otherTwin = twinNodes.find((t) => t.pid === rel.withPid);
      if (!otherTwin) return;

      const isConfirmedPair = otherTwin.twinRelations?.some(
        (r) => r.withPid === twin.pid && r.type === rel.type
      );
      if (!isConfirmedPair) return;

      processedPairs.add(pairKey);

      const twinBarX = getXOnDiagonal(twin.x, twin.y);
      const otherBarX = getXOnDiagonal(otherTwin.x, otherTwin.y);

      if (rel.type === TWIN_TYPE.monozygotic) {
        // Draw horizontal bar between the two diagonal lines
        const barStartX = Math.min(twinBarX, otherBarX);
        const barEndX = Math.max(twinBarX, otherBarX);
        path += ` M ${barStartX},${barY} L ${barEndX},${barY}`;
      } else if (rel.type === TWIN_TYPE.unknown) {
        // Record midpoint for "?" text marker
        unknownMarkers.push({ x: (twinBarX + otherBarX) / 2, y: barY });
      }
    });
  });

  return { path, unknownMarkers };
};
